/**
 * Короткая ссылка на текущий вид карты.
 *
 * Вид описан адресом целиком — в этом его сила: пересланная ссылка открывает в
 * точности тот же экран. Но переслать её нельзя: два десятка параметров ломаются
 * переносом строки в мессенджере, а продиктовать их по рации невозможно.
 * Сервер меняет адрес на семь символов и помнит обмен шесть часов.
 *
 * Хост к коду приписывает витрина, а не сервер: у кабинета два домена (ru и en),
 * бэкенд у них общий, и собирать адрес из заголовков за прокси — лишний способ
 * увести получателя на чужой язык.
 */
import { postJson } from '../api'

/** Ответ бэкенда: код и путь, но не хост. */
export interface ShareLinkResponse {
  code: string
  path: string
  expires_in: number
}

/** Адрес текущего вида: путь + параметры + якорь. */
export function viewAddress(loc: { pathname: string; search: string; hash: string }): string {
  return `${loc.pathname}${loc.search || ''}${loc.hash || ''}`
}

/** Код с сервера + хост витрины = то, что уходит в буфер обмена. */
export function shortLinkUrl(origin: string, path: string): string {
  return origin.replace(/\/+$/, '') + path
}

export interface CopyDeps {
  /** `navigator.clipboard.writeText`, если он вообще есть у этого браузера. */
  writeText?: (text: string) => Promise<void>
  /** Запасной путь через `document.execCommand('copy')`. */
  legacyCopy?: (text: string) => boolean
}

/** Скрытое поле + `execCommand`: буфера обмена нет на http и в старом Safari. */
function execCommandCopy(text: string): boolean {
  try {
    const area = document.createElement('textarea')
    area.value = text
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.opacity = '0'
    document.body.appendChild(area)
    area.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(area)
    return ok
  } catch {
    return false
  }
}

/**
 * Положить текст в буфер. Возвращает ЧЕСТНЫЙ результат.
 *
 * Кабинетом пользуются с планшета на мостике, где страница бывает открыта не по
 * https, а Safari спрашивает разрешение: «скопировано» при пустом буфере хуже,
 * чем показанная ссылка с предложением скопировать руками.
 */
export async function copyText(text: string, deps: CopyDeps = {}): Promise<boolean> {
  const write =
    deps.writeText ??
    (typeof navigator !== 'undefined' && navigator.clipboard
      ? (value: string) => navigator.clipboard.writeText(value)
      : undefined)
  if (write) {
    try {
      await write(text)
      return true
    } catch {
      /* отказ в разрешении — пробуем старый путь ниже */
    }
  }
  const legacy = deps.legacyCopy ?? execCommandCopy
  return legacy(text)
}

/** Попросить сервер о короткой ссылке на переданный адрес вида. */
export async function requestShortLink(address: string): Promise<ShareLinkResponse> {
  return postJson<ShareLinkResponse>('/api/v1/cabinet/share', { url: address })
}
